import React, { useState, useEffect } from "react";
import { FaPlus, FaEdit, FaTrash, FaProjectDiagram, FaTimes, FaFolderOpen, FaFileAlt, FaMoneyBillWave, FaUpload, FaCheck, FaChevronLeft, FaClipboardList, FaHardHat } from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import { useBuilding } from "../../context/BuildingContext";
import { db, auth } from "../../firebase";
import { storage } from "../../firebase";
import { useToast } from "../../components/Toast";
import { formatCurrencyUY } from "../../utils/formatters";
import { tolerantId } from "../../utils/firestoreHelpers";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, btnDanger, btnGhost, inputBase, pageContainer, pageTitle, modalOverlay } from "../../utils/designTokens";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const STATUS_OPTIONS = ["Planificado", "En Curso", "Pausado", "Finalizado", "Cancelado"];

const emptyForm = {
  name: "",
  description: "",
  status: "Planificado",
  contractor: "",
  budget: "",
  startDate: "",
  endDate: "",
};

const ProyectosAdmin = ({ setSelected }) => {
  const { buildings, selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Vista detalle / legajo
  const [detailProject, setDetailProject] = useState(null);
  const [detailTab, setDetailTab] = useState("resumen");
  const [projectDocs, setProjectDocs] = useState([]);
  const [projectBudgets, setProjectBudgets] = useState([]);
  const [projectExpenses, setProjectExpenses] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Categorías del checklist de documentación
  const LEGAJO_CATEGORIES = [
    { key: "bps", label: "BPS", items: ["Certificado de estar al día", "Planilla de trabajo", "Constancia de inscripción"] },
    { key: "dgi", label: "DGI", items: ["Certificado único", "Constancia RUT contratista", "Factura proforma"] },
    { key: "intendencia", label: "Intendencia", items: ["Permiso de obra", "Habilitación municipal", "Planos aprobados"] },
    { key: "min_trabajo", label: "Min. Trabajo", items: ["Seguro de accidentes", "Libro de obra", "Registro empresa"] },
    { key: "seguros", label: "Seguros", items: ["Póliza responsabilidad civil", "Todo riesgo construcción"] },
    { key: "contratos", label: "Contratos", items: ["Contrato con empresa", "Presupuesto aceptado", "Garantía"] },
  ];

  const buildingsToProcess = selectedBuilding
    ? buildings.filter((b) => b.buildingid === selectedBuilding.buildingid)
    : buildings;

  const fetchItems = async () => {
    if (!buildings || buildings.length === 0) { setLoading(false); return; }
    setLoading(true);
    try {
      let all = [];
      for (const b of buildingsToProcess) {
        const q = query(
          collection(db, "projects"),
          where("buildingid", "==", Number(b.buildingid))
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          buildingName: b.name,
        }));
        all = all.concat(data);
      }
      all.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      setItems(all);
    } catch (error) {
      console.error("Error al traer proyectos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [selectedBuilding, buildings]);

  const handleOpenCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || item.title || "",
      description: item.description || "",
      status: item.status || "Planificado",
      contractor: item.contractor || item.company || "",
      budget: item.budget ?? "",
      startDate: item.startDate || "",
      endDate: item.endDate || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedBuilding) { showToast("Selecciona un edificio primero", "warning"); return; }
    if (!form.name.trim()) { showToast("El nombre es obligatorio", "error"); return; }

    setSaving(true);
    try {
      const payload = {
        buildingid: Number(selectedBuilding.buildingid),
        name: form.name.trim(),
        title: form.name.trim(), // compatibilidad hacia atrás
        description: form.description.trim(),
        status: form.status,
        contractor: form.contractor.trim(),
        company: form.contractor.trim(), // compatibilidad hacia atrás
        budget: form.budget ? Number(form.budget) : null,
        startDate: form.startDate,
        endDate: form.endDate,
      };

      if (editing) {
        await updateDoc(doc(db, "projects", editing.id), payload);
      } else {
        await addDoc(collection(db, "projects"), payload);
      }

      setShowModal(false);
      setForm(emptyForm);
      setEditing(null);
      fetchItems();
    } catch (error) {
      console.error("Error al guardar proyecto:", error);
      showToast("Error al guardar: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "projects", id));
      setDeleteConfirm(null);
      fetchItems();
    } catch (error) {
      console.error("Error al eliminar proyecto:", error);
      showToast("Error al eliminar: " + error.message, "error");
    }
  };

  // Abrir detalle / legajo del proyecto
  const openDetail = async (project) => {
    setDetailProject(project);
    setDetailTab("resumen");
    setLoadingDetail(true);
    try {
      // Obtener documentos
      const docsSnap = await getDocs(query(collection(db, "projectDocuments"), where("projectId", "==", project.id)));
      setProjectDocs(docsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      // Obtener presupuestos
      const budgetsSnap = await getDocs(query(collection(db, "projectBudgets"), where("projectId", "==", project.id)));
      setProjectBudgets(budgetsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      // Obtener gastos vinculados con nombres de proveedor. La consulta fija el
      // edificio del proyecto: con las reglas 0D una consulta de expenses solo
      // por projectId se niega entera.
      const projectBid = project.buildingid ?? selectedBuilding?.buildingid;
      let rawExpenses = [];
      if (projectBid != null && projectBid !== "") {
        const expSnap = await getDocs(query(
          collection(db, "expenses"),
          where("projectId", "==", project.id),
          where("buildingid", "in", tolerantId(projectBid))
        ));
        rawExpenses = expSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
      // Enriquecer con nombres de proveedores
      const supplierIds = [...new Set(rawExpenses.map(e => e.supplierid).filter(Boolean))];
      // FIN-050: Batches de 10 para respetar limite de Firestore `in`
      let suppMap = {};
      for (let i = 0; i < supplierIds.length; i += 10) {
        const batch = supplierIds.slice(i, i + 10);
        const suppSnap = await getDocs(query(collection(db, "supplier"), where("supplierId", "in", batch)));
        suppSnap.docs.forEach(d => { const s = d.data(); suppMap[String(s.supplierId)] = s.name; });
      }
      setProjectExpenses(rawExpenses.map(e => ({ ...e, supplierName: suppMap[String(e.supplierid)] || "" })));
    } catch (err) {
      console.error("Error loading project detail:", err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUploadDoc = async (file, category, name) => {
    if (!file || !detailProject) return;
    setUploadingDoc(true);
    try {
      const path = `projects/${detailProject.id}/${category}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await addDoc(collection(db, "projectDocuments"), {
        projectId: detailProject.id,
        buildingid: Number(selectedBuilding.buildingid),
        category,
        name: name || file.name,
        fileName: file.name,
        fileType: file.type,
        fileUrl: url,
        uploadedBy: auth.currentUser?.email || "",
        uploadedAt: Timestamp.now(),
        notes: null,
      });

      // Refrescar documentos
      const docsSnap = await getDocs(query(collection(db, "projectDocuments"), where("projectId", "==", detailProject.id)));
      setProjectDocs(docsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      showToast("Documento subido", "success");
    } catch (err) {
      console.error("Error uploading:", err);
      showToast("Error al subir documento", "error");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleAddBudget = async (budgetData) => {
    if (!detailProject) return;
    try {
      await addDoc(collection(db, "projectBudgets"), {
        projectId: detailProject.id,
        buildingid: Number(selectedBuilding.buildingid),
        ...budgetData,
        createdAt: Timestamp.now(),
      });
      const budgetsSnap = await getDocs(query(collection(db, "projectBudgets"), where("projectId", "==", detailProject.id)));
      setProjectBudgets(budgetsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      showToast("Presupuesto agregado", "success");
    } catch (err) {
      console.error("Error adding budget:", err);
      showToast("Error al agregar presupuesto", "error");
    }
  };

  const handleAcceptBudget = async (budgetId) => {
    try {
      // Desmarcar todos los aceptados primero, luego marcar el seleccionado
      for (const b of projectBudgets) {
        if (b.status === "aceptado") {
          await updateDoc(doc(db, "projectBudgets", b.id), { status: "recibido" });
        }
      }
      await updateDoc(doc(db, "projectBudgets", budgetId), { status: "aceptado" });
      const budgetsSnap = await getDocs(query(collection(db, "projectBudgets"), where("projectId", "==", detailProject.id)));
      setProjectBudgets(budgetsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      showToast("Presupuesto aceptado", "success");
    } catch (err) {
      console.error("Error:", err);
    }
  };

  // Calcular completitud del legajo
  const getLegajoCompleteness = () => {
    let total = 0, completed = 0;
    LEGAJO_CATEGORIES.forEach((cat) => {
      cat.items.forEach((item) => {
        total++;
        const hasDoc = projectDocs.some((d) => d.category === cat.key && d.name === item);
        if (hasDoc) completed++;
      });
    });
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const statusColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "planificado" || s === "pendiente") return { bg: colors.infoLight, color: colors.infoDark };
    if (s === "en curso" || s === "en progreso") return { bg: colors.warningLight, color: colors.warningDark };
    if (s === "finalizado") return { bg: colors.successLight, color: colors.successDark };
    if (s === "pausado") return { bg: colors.warningLight, color: colors.warningDark };
    if (s === "cancelado") return { bg: colors.errorLight, color: colors.errorDark };
    return { bg: colors.gray100, color: colors.gray900 };
  };

  // Helper para obtener nombre (soporta tanto 'title' antiguo como 'name' nuevo)
  const getProjectName = (item) => item.name || item.title || "Sin nombre";
  const getProjectContractor = (item) => item.contractor || item.company || "";
  const getProjectBudget = (item) => Number(item.budget) || 0;

  // Cálculos KPI
  const totalBudget = items.reduce((s, i) => s + getProjectBudget(i), 0);
  const enCurso = items.filter(i => {
    const s = (i.status || "").toLowerCase();
    return s === "en curso" || s === "en progreso";
  }).length;
  const totalGastado = 0; // Se calculará cuando existan gastos con projectId

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div style={styles.headerTitleSection}>
          <h2 style={styles.pageTitle}>
            <FaHardHat style={{ marginRight: spacing[2] }} />
            Obras y Proyectos
          </h2>
          <p style={styles.pageSubtitle}>
            {selectedBuilding
              ? `${selectedBuilding.name} · ${items.length} proyecto${items.length !== 1 ? "s" : ""}`
              : `${buildingsToProcess.length} edificio${buildingsToProcess.length > 1 ? "s" : ""} · ${items.length} proyectos`}
          </p>
        </div>
        <button
          style={{
            ...styles.addBtn,
            opacity: !selectedBuilding ? 0.5 : 1,
            cursor: !selectedBuilding ? "not-allowed" : "pointer",
          }}
          disabled={!selectedBuilding}
          onClick={handleOpenCreate}
        >
          <FaPlus size={14} /> Nuevo Proyecto
        </button>
      </div>

      {/* Tarjetas KPI */}
      {selectedBuilding && !loading && items.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: spacing[4], marginBottom: spacing[6] }}>
          {[
            { label: "Total Proyectos", value: items.length, color: colors.black },
            { label: "En Curso", value: enCurso, color: colors.warning },
            { label: "Presupuesto Total", value: formatCurrencyUY(totalBudget), color: colors.primary },
            { label: "Total Gastado", value: formatCurrencyUY(totalGastado), color: colors.success },
          ].map((kpi, i) => (
            <div key={i} style={{ ...cardBase, padding: `${spacing[5]}px ${spacing[6]}px` }}>
              <div style={{ fontSize: typography.sizes.base, color: colors.gray600, marginBottom: spacing[2] }}>{kpi.label}</div>
              <div style={{ fontSize: 24, fontWeight: 400, color: kpi.color }}>{kpi.value}</div>
            </div>
          ))}
        </div>
      )}

      {!selectedBuilding && (
        <div style={styles.selectBuildingNotice}>
          <p>Selecciona un edificio desde el filtro superior para ver y crear proyectos.</p>
        </div>
      )}

      {loading && selectedBuilding ? (
        <div style={styles.selectBuildingNotice}><p>Cargando...</p></div>
      ) : selectedBuilding && items.length === 0 ? (
        <div style={styles.emptyState}>
          <FaHardHat size={40} color={colors.gray300} />
          <p style={styles.emptyText}>No hay proyectos para este edificio.</p>
          <button style={styles.emptyBtn} onClick={handleOpenCreate}>
            <FaPlus size={12} /> Crear primer proyecto
          </button>
        </div>
      ) : selectedBuilding ? (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Nombre</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Presupuesto</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Gastado</th>
                <th style={styles.th}>Avance (%)</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Fechas</th>
                <th style={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const sc = statusColor(item.status);
                const budget = getProjectBudget(item);
                const spent = 0; // desde gastos vinculados
                const avance = budget > 0 ? Math.round((spent / budget) * 100) : 0;
                return (
                  <tr key={item.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div>
                        <div style={{ fontWeight: 400 }}>{getProjectName(item)}</div>
                        {getProjectContractor(item) && (
                          <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginTop: 2 }}>
                            Contratista: {getProjectContractor(item)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: 400 }}>
                      {budget > 0 ? formatCurrencyUY(budget) : "-"}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: 400, color: colors.success }}>
                      {formatCurrencyUY(spent)}
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                        <span>{avance}%</span>
                        <div style={{ flex: 1, height: 6, background: colors.gray200, borderRadius: 3, maxWidth: 80 }}>
                          <div style={{ height: "100%", width: `${avance}%`, background: colors.primary, borderRadius: 3 }} />
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: sc.bg,
                        color: sc.color,
                      }}>
                        {item.status || "Planificado"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>
                        {item.startDate && <div>Inicio: {item.startDate}</div>}
                        {item.endDate && <div>Fin: {item.endDate}</div>}
                        {!item.startDate && !item.endDate && "-"}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button style={{ ...styles.iconBtn, background: colors.primarySubtle, borderRadius: 6, padding: "4px 8px" }} onClick={() => openDetail(item)} title="Ver legajo">
                          <FaFolderOpen color={colors.primary} size={14} />
                        </button>
                        <button style={styles.iconBtn} onClick={() => handleOpenEdit(item)} title="Editar">
                          <FaEdit color={colors.primary} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Vista detalle / legajo */}
      {detailProject && (
        <div style={styles.detailOverlay} onClick={() => setDetailProject(null)}>
          <div style={{ ...styles.modalContent, maxWidth: 800, maxHeight: "92vh" }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                <button style={{ ...styles.iconBtn, padding: 4 }} onClick={() => setDetailProject(null)}>
                  <FaChevronLeft size={14} color={colors.gray600} />
                </button>
                <h3 style={styles.modalTitle}>{getProjectName(detailProject)}</h3>
                <span style={{ ...styles.badge, backgroundColor: statusColor(detailProject.status).bg, color: statusColor(detailProject.status).color }}>
                  {detailProject.status}
                </span>
              </div>
              <button style={styles.closeBtn} onClick={() => setDetailProject(null)} aria-label="Cerrar"><FaTimes /></button>
            </div>

            {/* Pestañas */}
            <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${colors.gray200}`, padding: `0 ${spacing[5]}px` }}>
              {[
                { key: "resumen", label: "Resumen", icon: FaClipboardList },
                { key: "documentacion", label: "Documentación", icon: FaFileAlt },
                { key: "pagos", label: "Pagos", icon: FaMoneyBillWave },
                { key: "presupuestos", label: "Presupuestos", icon: FaFileAlt },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setDetailTab(tab.key)}
                  style={{
                    padding: `${spacing[3]}px ${spacing[4]}px`, fontSize: typography.sizes.base, fontWeight: 400, cursor: "pointer",
                    background: "none", border: "none", borderBottom: detailTab === tab.key ? `2px solid ${colors.primary}` : "2px solid transparent",
                    color: detailTab === tab.key ? colors.primary : colors.gray600,
                    display: "flex", alignItems: "center", gap: spacing[2],
                    transition: `all ${transitions.fast}`,
                  }}
                >
                  <tab.icon size={12} /> {tab.label}
                </button>
              ))}
            </div>

            <div style={{ padding: spacing[5], overflowY: "auto", maxHeight: "calc(92vh - 140px)" }}>
              {loadingDetail ? (
                <p style={{ color: colors.gray600, textAlign: "center", padding: spacing[10] }}>Cargando...</p>
              ) : detailTab === "resumen" ? (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[4], marginBottom: spacing[5] }}>
                    <div><strong style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Contratista:</strong><div style={{ fontSize: typography.sizes.md }}>{getProjectContractor(detailProject) || "-"}</div></div>
                    <div><strong style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Presupuesto:</strong><div style={{ fontSize: typography.sizes.md }}>{getProjectBudget(detailProject) > 0 ? formatCurrencyUY(getProjectBudget(detailProject)) : "-"}</div></div>
                    <div><strong style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Fecha inicio:</strong><div style={{ fontSize: typography.sizes.md }}>{detailProject.startDate || "-"}</div></div>
                    <div><strong style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Fecha fin:</strong><div style={{ fontSize: typography.sizes.md }}>{detailProject.endDate || "-"}</div></div>
                    <div style={{ gridColumn: "1 / -1" }}><strong style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Descripción:</strong><div style={{ fontSize: typography.sizes.md }}>{detailProject.description || "-"}</div></div>
                  </div>
                  {/* Completitud del legajo */}
                  <div style={{ background: colors.gray50, borderRadius: radius.md, padding: spacing[4], marginBottom: spacing[4] }}>
                    <div style={{ fontSize: typography.sizes.base, fontWeight: 400, color: colors.gray900, marginBottom: spacing[2] }}>Legajo completo al {getLegajoCompleteness()}%</div>
                    <div style={{ height: 8, background: colors.gray200, borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${getLegajoCompleteness()}%`, background: getLegajoCompleteness() === 100 ? colors.success : colors.primary, borderRadius: 4, transition: `width ${transitions.slow}` }} />
                    </div>
                  </div>
                  {/* Resumen de presupuesto */}
                  {projectBudgets.length > 0 && (
                    <div style={{ background: colors.gray50, borderRadius: radius.md, padding: spacing[4] }}>
                      <div style={{ fontSize: typography.sizes.base, fontWeight: 400, color: colors.gray900, marginBottom: spacing[2] }}>Presupuesto</div>
                      {(() => {
                        const accepted = projectBudgets.find((b) => b.status === "aceptado");
                        const totalSpent = projectExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
                        const budget = accepted ? Number(accepted.amount) || 0 : 0;
                        return (
                          <div style={{ display: "flex", gap: spacing[5] }}>
                            <div><div style={{ fontSize: typography.sizes.xs, color: colors.gray600 }}>Estimado</div><div style={{ fontSize: typography.sizes.lg, fontWeight: 400, color: colors.primary }}>{budget > 0 ? formatCurrencyUY(budget) : "-"}</div></div>
                            <div><div style={{ fontSize: typography.sizes.xs, color: colors.gray600 }}>Ejecutado</div><div style={{ fontSize: typography.sizes.lg, fontWeight: 400, color: totalSpent > budget && budget > 0 ? colors.error : colors.success }}>{formatCurrencyUY(totalSpent)}</div></div>
                            {budget > 0 && <div><div style={{ fontSize: typography.sizes.xs, color: colors.gray600 }}>Progreso</div><div style={{ fontSize: typography.sizes.lg, fontWeight: 400 }}>{Math.round((totalSpent / budget) * 100)}%</div></div>}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : detailTab === "documentacion" ? (
                <div>
                  {LEGAJO_CATEGORIES.map((cat) => (
                    <div key={cat.key} style={{ marginBottom: spacing[5] }}>
                      <div style={{ fontSize: typography.sizes.base, fontWeight: 400, color: colors.gray900, marginBottom: spacing[2], textTransform: "uppercase", letterSpacing: "0.5px" }}>{cat.label}</div>
                      {cat.items.map((itemName) => {
                        const existingDoc = projectDocs.find((d) => d.category === cat.key && d.name === itemName);
                        return (
                          <div key={itemName} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${colors.gray100}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                              {existingDoc ? <FaCheck size={12} color={colors.success} /> : <div style={{ width: 12, height: 12, border: `2px solid ${colors.gray300}`, borderRadius: 3 }} />}
                              <span style={{ fontSize: typography.sizes.base, color: existingDoc ? colors.success : colors.gray900 }}>{itemName}</span>
                            </div>
                            {existingDoc ? (
                              <a href={existingDoc.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: typography.sizes.xs, color: colors.primary, textDecoration: "none" }}>Ver archivo</a>
                            ) : (
                              <label style={{ fontSize: typography.sizes.xs, color: colors.primary, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                                <FaUpload size={10} /> Subir
                                <input type="file" style={{ display: "none" }} onChange={(e) => {
                                  if (e.target.files[0]) handleUploadDoc(e.target.files[0], cat.key, itemName);
                                }} />
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {/* Documentos adicionales */}
                  <div style={{ marginTop: spacing[4] }}>
                    <div style={{ fontSize: typography.sizes.base, fontWeight: 400, color: colors.gray900, marginBottom: spacing[2] }}>OTROS DOCUMENTOS</div>
                    {projectDocs.filter((d) => d.category === "otros").map((d) => (
                      <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${colors.gray100}` }}>
                        <span style={{ fontSize: typography.sizes.base }}>{d.name || d.fileName}</span>
                        <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: typography.sizes.xs, color: colors.primary, textDecoration: "none" }}>Ver</a>
                      </div>
                    ))}
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: spacing[2], padding: `6px ${spacing[3]}px`, background: colors.gray100, borderRadius: radius.sm, fontSize: typography.sizes.sm, color: colors.gray900, cursor: "pointer" }}>
                      <FaPlus size={10} /> Agregar documento
                      <input type="file" style={{ display: "none" }} onChange={(e) => {
                        if (e.target.files[0]) handleUploadDoc(e.target.files[0], "otros", e.target.files[0].name);
                      }} />
                    </label>
                  </div>
                  {uploadingDoc && <p style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginTop: spacing[2] }}>Subiendo...</p>}
                </div>
              ) : detailTab === "pagos" ? (
                <div>
                  <p style={{ fontSize: typography.sizes.base, color: colors.gray600, marginBottom: spacing[3] }}>Gastos vinculados a este proyecto — se descuentan del fondo de reserva</p>
                  {projectExpenses.length === 0 ? (
                    <p style={{ color: colors.gray400, fontSize: typography.sizes.base }}>No hay gastos vinculados a este proyecto.</p>
                  ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: typography.sizes.base }}>
                      <thead><tr style={{ borderBottom: `1px solid ${colors.gray200}` }}>
                        <th style={styles.detailTh}>Nº Comp.</th>
                        <th style={styles.detailTh}>Descripción</th>
                        <th style={styles.detailTh}>Proveedor</th>
                        <th style={styles.detailTh}>Fecha</th>
                        <th style={{ ...styles.detailTh, textAlign: "right" }}>Monto</th>
                        <th style={{ ...styles.detailTh, textAlign: "center" }}>Estado</th>
                        <th style={{ ...styles.detailTh, textAlign: "center" }}>Comprobante</th>
                      </tr></thead>
                      <tbody>
                        {projectExpenses.map((e) => (
                          <tr key={e.id} style={{ borderBottom: `1px solid ${colors.gray100}` }}>
                            <td style={{ padding: "8px 10px", fontSize: typography.sizes.sm }}>{e.receiptNumber || "-"}</td>
                            <td style={{ padding: "8px 10px" }}>{e.description || "-"}</td>
                            <td style={{ padding: "8px 10px", fontSize: typography.sizes.sm }}>{e.supplierName || "-"}</td>
                            <td style={{ padding: "8px 10px" }}>{e.invoiceDate || "-"}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 400 }}>{formatCurrencyUY(e.amount)}</td>
                            <td style={{ padding: "8px 10px", textAlign: "center" }}>
                              <span style={{ fontSize: typography.sizes.xs, fontWeight: 400, padding: "2px 8px", borderRadius: radius.md, background: e.paid ? colors.successLight : colors.warningLight, color: e.paid ? colors.success : colors.warning }}>
                                {e.paid ? "Pagado" : "Pendiente"}
                              </span>
                            </td>
                            <td style={{ padding: "8px 10px", textAlign: "center" }}>
                              {e.bill ? (
                                <a href={e.bill} target="_blank" rel="noopener noreferrer" style={{ color: colors.info, fontSize: typography.sizes.sm, fontWeight: 400 }}>Ver</a>
                              ) : <span style={{ color: colors.gray400, fontSize: typography.sizes.xs }}>—</span>}
                            </td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: `2px solid ${colors.gray200}`, fontWeight: 400 }}>
                          <td colSpan={4} style={{ padding: "8px 10px" }}>TOTAL</td>
                          <td style={{ padding: "8px 10px", textAlign: "right" }}>{formatCurrencyUY(projectExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0))}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              ) : detailTab === "presupuestos" ? (
                <div>
                  {projectBudgets.length === 0 ? (
                    <p style={{ color: colors.gray400, fontSize: typography.sizes.base }}>No hay presupuestos cargados.</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
                      {projectBudgets.map((b) => (
                        <div key={b.id} style={{ ...cardBase, padding: spacing[4], background: b.status === "aceptado" ? colors.successLight : colors.white }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <div style={{ fontWeight: 400, fontSize: typography.sizes.md }}>{b.company || "Sin empresa"}</div>
                              <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>{b.date || "-"} — {b.currency === "dolar" ? "USD" : "UYU"}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: typography.sizes.lg, fontWeight: 400 }}>{formatCurrencyUY(b.amount)}</div>
                              <span style={{ fontSize: typography.sizes.xs, fontWeight: 400, color: b.status === "aceptado" ? colors.success : b.status === "rechazado" ? colors.error : colors.warning, background: b.status === "aceptado" ? colors.successLight : b.status === "rechazado" ? colors.errorLight : colors.warningLight, padding: "2px 8px", borderRadius: radius.md }}>
                                {b.status === "aceptado" ? "Aceptado" : b.status === "rechazado" ? "Rechazado" : "Recibido"}
                              </span>
                            </div>
                          </div>
                          {b.status !== "aceptado" && (
                            <button onClick={() => handleAcceptBudget(b.id)} style={{ marginTop: spacing[2], padding: `4px ${spacing[3]}px`, fontSize: typography.sizes.sm, fontWeight: 400, background: colors.success, color: colors.white, border: "none", borderRadius: 6, cursor: "pointer" }}>
                              Aceptar presupuesto
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Formulario para agregar presupuesto */}
                  <div style={{ marginTop: spacing[4], borderTop: `1px solid ${colors.gray200}`, paddingTop: spacing[4] }}>
                    <div style={{ fontSize: typography.sizes.base, fontWeight: 400, color: colors.gray900, marginBottom: spacing[2] }}>Agregar presupuesto</div>
                    <BudgetForm onSubmit={handleAddBudget} />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <AdminModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Editar Proyecto" : "Nuevo Proyecto"}
        size="medium"
        footer={
          <>
            <button style={styles.cancelBtn} onClick={() => setShowModal(false)} disabled={saving}>
              Cancelar
            </button>
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? "Guardando..." : editing ? "Actualizar" : "Crear"}
            </button>
          </>
        }
      >
        <div style={styles.buildingInfo}>
          <span style={styles.buildingLabel}>Edificio:</span>
          <span style={styles.buildingName}>{selectedBuilding?.name}</span>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Nombre *</label>
          <input
            type="text"
            style={styles.input}
            placeholder="Ej: Cambio de cañerías"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Descripción</label>
          <textarea
            style={styles.textarea}
            placeholder="Descripción del proyecto"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[4] }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Contratista / Empresa</label>
            <input
              type="text"
              style={styles.input}
              placeholder="Nombre del contratista"
              value={form.contractor}
              onChange={(e) => setForm({ ...form, contractor: e.target.value })}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Presupuesto ($)</label>
            <input
              type="number"
              style={styles.input}
              placeholder="50000"
              value={form.budget}
              onChange={(e) => setForm({ ...form, budget: e.target.value })}
            />
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Estado</label>
          <select
            style={styles.input}
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[4] }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Fecha de inicio</label>
            <input
              type="date"
              style={styles.input}
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Fecha de finalización</label>
            <input
              type="date"
              style={styles.input}
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </div>
        </div>
      </AdminModal>
    </div>
  );
};

// ─── Estilos con design tokens ──────────────────────────────────────────────
const styles = {
  page: {
    ...pageContainer,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    ...pageTitle,
    fontSize: typography.sizes.xl,
    display: "flex",
    alignItems: "center",
  },
  pageSubtitle: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
  },
  addBtn: {
    ...btnPrimary,
  },
  selectBuildingNotice: {
    ...cardBase,
    padding: spacing[10],
    textAlign: "center",
    color: colors.gray600,
    fontSize: typography.sizes.md,
  },
  emptyState: {
    ...cardBase,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
    gap: spacing[3],
  },
  emptyText: {
    color: colors.gray600,
    fontSize: typography.sizes.md,
    margin: 0,
  },
  emptyBtn: {
    ...btnPrimary,
    marginTop: spacing[2],
  },
  tableWrapper: {
    ...cardBase,
    padding: 0,
    overflow: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray600,
    borderBottom: `1px solid ${colors.gray200}`,
    background: colors.gray50,
  },
  tr: {
    borderBottom: `1px solid ${colors.gray100}`,
  },
  td: {
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
    color: colors.black,
  },
  badge: {
    padding: "4px 10px",
    borderRadius: radius.md,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
  },
  iconBtn: {
    ...btnGhost,
    padding: 6,
    borderRadius: 6,
  },
  confirmDelete: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  confirmYes: {
    ...btnDanger,
    background: colors.error,
    color: colors.white,
    padding: "4px 10px",
    fontSize: typography.sizes.sm,
  },
  confirmNo: {
    ...btnSecondary,
    background: colors.gray200,
    color: colors.gray900,
    padding: "4px 10px",
    fontSize: typography.sizes.sm,
  },
  // Overlay para el modal de detalle (legajo)
  detailOverlay: {
    ...modalOverlay,
  },
  modalContent: {
    backgroundColor: colors.white,
    borderRadius: radius.md,
    width: "100%",
    maxWidth: 500,
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: shadows.modal,
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${spacing[4]}px ${spacing[5]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  modalTitle: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
    margin: 0,
  },
  closeBtn: {
    ...btnGhost,
    color: colors.gray600,
    padding: 4,
  },
  modalBody: {
    padding: spacing[5],
  },
  buildingInfo: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    marginBottom: spacing[5],
    padding: `${spacing[3]}px ${spacing[4]}px`,
    background: colors.primarySubtle,
    borderRadius: radius.sm,
  },
  buildingLabel: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
  },
  buildingName: {
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.primary,
  },
  formGroup: {
    marginBottom: spacing[4],
  },
  label: {
    display: "block",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray900,
    marginBottom: 6,
  },
  input: {
    ...inputBase,
    boxSizing: "border-box",
  },
  textarea: {
    ...inputBase,
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: spacing[3],
    padding: `${spacing[4]}px ${spacing[5]}px`,
    borderTop: `1px solid ${colors.gray200}`,
  },
  cancelBtn: {
    ...btnSecondary,
  },
  saveBtn: {
    ...btnPrimary,
  },
  // Encabezado de tabla en vista detalle (pagos)
  detailTh: {
    textAlign: "left",
    padding: "8px 10px",
    fontSize: typography.sizes.xs,
    color: colors.gray600,
    fontWeight: 400,
  },
};

// Componente simple de formulario de presupuesto
const BudgetForm = ({ onSubmit }) => {
  const [form, setForm] = useState({ company: "", amount: "", currency: "peso", date: "", notes: "" });
  const handleSubmit = () => {
    if (!form.company || !form.amount) return;
    onSubmit({ ...form, amount: Number(form.amount), status: "recibido" });
    setForm({ company: "", amount: "", currency: "peso", date: "", notes: "" });
  };

  // Estilo para los inputs del formulario de presupuesto
  const budgetInputStyle = { ...inputBase, padding: "8px 10px", fontSize: typography.sizes.base, boxSizing: "border-box" };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3] }}>
      <input type="text" placeholder="Empresa" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} style={budgetInputStyle} />
      <input type="number" placeholder="Monto" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={budgetInputStyle} />
      <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={budgetInputStyle}>
        <option value="peso">UYU</option>
        <option value="dolar">USD</option>
      </select>
      <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={budgetInputStyle} />
      <div style={{ gridColumn: "1 / -1" }}>
        <button onClick={handleSubmit} style={{ ...btnPrimary, padding: `${spacing[2]}px ${spacing[4]}px`, fontSize: typography.sizes.base }}>
          <FaPlus size={10} /> Agregar
        </button>
      </div>
    </div>
  );
};

export default ProyectosAdmin;
